import { memo, useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Clock, Eye, BookOpen } from 'lucide-react';
import { GameVariant, VARIANT_SPECS, ACTIVE_VARIANTS, toGameVariant, isKnownVariant, GAME_MODES, type GameMode } from '@draughts/shared';
import { gameModeLabel } from '../../utils/challenge-labels';
import { gameModeLeading } from '../../utils/challenge-icons';
import VademecumVariante from './VademecumVariante';
import './GameHeader.css';

export interface GameHeaderProps {
  variant: string;
  variantLabel: string;
  timeLabel: string;
  rated: boolean;
  /** Win-condition modifier; a badge is shown for non-normal modes. */
  ruleMode?: string;
  /*
   * Celle in più, nella stessa riga. Le usa l'arena per dire a che punto sei
   * (tempo che resta, posizione, punti) senza aggiungere una striscia sopra:
   * una riga in più costa altezza, e in questa schermata l'altezza è quel che
   * resta alla damiera.
   */
  extraCells?: ReactNode;
  /*
   * Chi e' davanti a questa damiera la sta GUARDANDO, non giocando. Senza questa
   * cella lo spettatore lo capiva solo in sottrazione — niente Ritiro, niente
   * Patta, «Esci» al loro posto — che e' il modo peggiore di capirlo.
   *
   * Un booleano e non una cella passata da fuori: questo componente e' `memo`
   * perche' l'orologio ridisegna GameScreen cinque volte al secondo, e un
   * elemento JSX inline sarebbe una prop nuova a ogni tick.
   */
  spectating?: boolean;
  /**
   * L'intestazione dentro la COLONNA DESTRA, su desktop.
   *
   * Qui la riga non ha 800px ma 316 di colonna, e il suo contenuto ne misura
   * ~500: il `flex-wrap: nowrap` di base la farebbe scorrere di lato, che e'
   * peggio del difetto che stiamo togliendo. Il `nowrap` nasce contro un
   * `flex-wrap: wrap` morto in App.css che in una riga LARGA portava
   * l'intestazione da 29 a 64px, rubando 35px alla damiera; in una colonna
   * stretta quel problema non esiste e andare a capo e' la cosa giusta.
   */
  colonna?: boolean;
}

export const GameHeader = memo<GameHeaderProps>(({ variant, variantLabel, timeLabel, rated, ruleMode, extraCells, spectating = false, colonna = false }) => {
  const { t } = useTranslation();
  /*
   * Stesso ragionamento del badge di `ruleMode` qui sotto, applicato all'unico
   * pezzo che ancora mentiva: l'icona. `toGameVariant` ripiega su INTERNATIONAL
   * per qualunque stringa che questa build non conosce, quindi un dato sbagliato
   * arrivato dal filo si travestiva da globo — da partita internazionale
   * legittima. Con il `⚠` si vede per quel che e'. (L'etichetta la rende gia'
   * grezza GameScreen, col Title-case come `defaultValue`.)
   */
  const varianteNota = isKnownVariant(variant);
  const gv = toGameVariant(variant);
  // Il badge segnala che si sta giocando una modalità diversa da quella normale — e le
  // legge da GAME_MODES invece di elencarle, perché la catena scritta a mano che c'era
  // qui avrebbe taciuto ogni modalità aggiunta dopo. Il prop è una stringa che arriva dal
  // filo: un valore che questa build non conosce non merita un badge, o si vedrebbe una
  // pastiglia etichettata "Normale".
  const showMode = !!ruleMode && ruleMode !== 'normal' && GAME_MODES.includes(ruleMode as GameMode);
  /* Senza secondo argomento di proposito: i default inline che stavano qui erano
   * le stringhe inglesi che sono state spedite in tutte e sette le lingue finche'
   * `game.header.*` non e' esistito in nessun locale. */
  const ratedLabel = rated ? t('game.header.rated') : t('game.header.casual');
  const spectatingLabel = t('spectate.watching');

  /*
   * IL VADEMECUM: chi gioca una variante che non conosce vuole le regole
   * principali subito, e non ha nessun altro posto in partita dove trovarle.
   *
   * Lo stato sta QUI e non arriva da fuori: questo componente e' `memo` perche'
   * l'orologio ridisegna GameScreen cinque volte al secondo, e una callback
   * passata dal contenitore sarebbe una prop nuova a ogni tick.
   *
   * La condizione e' doppia, e tutt'e due i congiunti servono. `isKnownVariant`
   * risponde «so cosa significa», non «si puo' giocare»: e' vera anche per HEX,
   * che sta fuori dalle varianti attive e ha `piecesPerPlayer: 0` — il pannello
   * annuncerebbe «0 pezzi per lato». E il solo `ACTIVE_VARIANTS` non basta
   * perche' `toGameVariant` ripiega su INTERNATIONAL, che e' attiva, per
   * qualunque stringa che questa build non conosce: una variante inventata
   * aprirebbe il vademecum dell'internazionale.
   */
  const vademecumPossibile = varianteNota && ACTIVE_VARIANTS.includes(gv);
  const [vademecumAperto, setVademecumAperto] = useState(false);
  const apriVademecum = useCallback(() => setVademecumAperto(true), []);
  /* In `useCallback` per davvero: in arena `extraCells` e' un elemento nuovo a
     ogni tick dell'orologio, quindi `memo` non trattiene niente e questa riga
     si ridisegna cinque volte al secondo. Senza, l'effetto che ascolta Tab ed
     Escape dentro `useModaleAccessibile` si ri-sottoscriverebbe altrettante
     volte, perche' dipende dall'identita' di `onClose`. */
  const chiudiVademecum = useCallback(() => setVademecumAperto(false), []);

  const iconaVariante = (
    <span className="game-header__variant-icon" aria-hidden="true">
      {!varianteNota
        ? <span>⚠</span>
        : gv === GameVariant.INTERNATIONAL
          ? <Globe size={14} />
          : <span>{VARIANT_SPECS[gv].ui.flagEmoji}</span>}
    </span>
  );

  /*
   * Il travestimento del bottone e l'area di tocco sono le due classi che il
   * foglio ha gia' (le usano il punteggio del torneo e quello dell'arena):
   * `--apri` lo veste come la scritta che era, `--tap` gli da' i 44px.
   *
   * `aria-label` e non il solo `title`: il contenuto testuale vince sul `title`
   * nel calcolo del nome accessibile, quindi senza di questo il bottone si
   * annuncerebbe «Italiana, pulsante» e nessuno saprebbe cosa apre. Il nome
   * CONTIENE l'etichetta visibile, come fa la cella dell'arena, cosi' chi
   * comanda a voce puo' ancora chiedere la cosa che vede.
   */
  const cellaVariante = vademecumPossibile ? (
    <button
      type="button"
      className="game-header__cell game-header__cell--tap game-header__cell--apri game-header__variant"
      data-testid="game-header-variant"
      aria-haspopup="dialog"
      aria-label={`${variantLabel}: ${t('game.header.variantRules')}`}
      onClick={apriVademecum}
    >
      {iconaVariante}
      {/* La PAROLA sta in un suo nodo perche' e' l'unica cosa qui dentro che
          puo' cedere: vedi `.game-header__variant-text` nel foglio. La bandiera
          e il libro no — sono l'indizio che si preme, e mezzo indizio non dice
          niente. */}
      <span className="game-header__variant-text">{variantLabel}</span>
      <BookOpen size={13} aria-hidden="true" />
    </button>
  ) : (
    <span className="game-header__cell game-header__variant" data-testid="game-header-variant">
      {iconaVariante}
      {variantLabel}
    </span>
  );

  return (
    <div className={`game-header${extraCells ? ' game-header--with-cells' : ''}${colonna ? ' game-header--colonna' : ''}`} data-testid="game-header">
      {/* PRIMA di tutto il resto, e non in coda: quando le celle non ci stanno
          questa riga scorre, e `justify-content: safe center` fa ripiegare il
          traboccamento su flex-start — quindi la prima cella e' l'unica che resta
          visibile per costruzione. Sotto i 480px il CSS lascia solo l'occhio e
          porta la parola fuori campo senza toglierla dall'albero (il `title` e il
          testo restano per chi legge con lo screen reader). */}
      {spectating && (
        <>
          <span
            className="game-header__cell game-header__spectating"
            data-testid="game-header-spectating"
            title={spectatingLabel}
          >
            <Eye size={14} aria-hidden="true" />
            <span className="game-header__spectating-text">{spectatingLabel}</span>
          </span>
          <span className="game-header__divider" aria-hidden="true" />
        </>
      )}
      <span className="game-header__cell game-header__time" data-testid="game-header-time">
        <Clock size={14} aria-hidden="true" />
        {timeLabel}
      </span>
      <span className="game-header__divider" aria-hidden="true" />
      <span
        className={`game-header__cell game-header__rated ${rated ? 'game-header__rated--on' : 'game-header__rated--off'}`}
        data-testid="game-header-rated"
        title={ratedLabel}
      >
        <span className="game-header__rated-dot" aria-hidden="true" />
        {/* Sotto i 480px il CSS lascia solo il pallino e porta questa parola fuori
            campo (senza toglierla dall'albero): e' la cella che cede per prima
            perche' e' l'unica delle tre a stare tutta nel colore del pallino. */}
        <span className="game-header__rated-text">{ratedLabel}</span>
      </span>
      {extraCells && (
        <>
          <span className="game-header__divider" aria-hidden="true" />
          {extraCells}
        </>
      )}
      {showMode && (
        <>
          <span className="game-header__divider" aria-hidden="true" />
          <span className="game-header__cell game-header__mode" data-testid="game-header-mode">
            {gameModeLeading(ruleMode as string, 14)}
            {gameModeLabel(t, ruleMode as string)}
          </span>
        </>
      )}
      {/* LA VARIANTE STA IN CODA, e fino a ieri stava in testa.
          `justify-content: safe center` ripiega il traboccamento su flex-start,
          quindi in una riga che scorre e' l'ULTIMA cella quella che esce dal
          bordo destro. Da quando la variante si preme e' un bersaglio da
          toccare, e fra lei e il bottone della classifica d'arena deve cedere
          lei: le regole della variante non cambiano per tutta la partita,
          mentre la classifica si muove mentre giochi.
          Cedere, non sparire: la cella si stringe coi puntini prima di
          scorrere via (vedi `.game-header__variant` nel foglio). Misurato a
          360px in una partita col computer — dove l'etichetta del tempo e'
          «Senza Limite di Tempo», 157px da sola — la riga adesso NON scorre
          affatto: prima di questo lavoro scorreva di 23px, col pallino del
          rated 6px oltre il bordo. */}
      <span className="game-header__divider" aria-hidden="true" />
      {cellaVariante}
      {/* Il pannello si monta solo da aperto e vive in un PORTALE su
          document.body, quindi non e' un figlio di questa riga: ne' in DOM (la
          riga taglia e sfoca: vedi VademecumVariante) ne' per i test che
          tengono fermo l'ordine delle celle. */}
      {vademecumAperto && vademecumPossibile && (
        <VademecumVariante
          variant={gv}
          variantLabel={variantLabel}
          bendata={ruleMode === 'blind'}
          onClose={chiudiVademecum}
        />
      )}
    </div>
  );
});

GameHeader.displayName = 'GameHeader';
